import React, { useEffect, useState } from "react";
import { patchScene, getSharedScene } from "../lib/plbxApi";

/**
 * Floating title chip rendered top-left next to Excalidraw's hamburger menu.
 *
 * Two modes:
 *   - team board (`window.__plbxBoardId` set): clickable, prompts to rename.
 *   - share viewer (`#board=<token>`): read-only name + "shared" hint.
 *
 * If no active board, renders nothing — stock blank canvas stays clean.
 *
 * Mount lives inside ExcalidrawWrapper alongside PlbxBoardSync (patch 03).
 * Name is sourced from `window.__plbxBoardName` which PlbxBoardSync sets on
 * hydrate; for share viewer we fetch the public scene to pull `name`.
 */
export const PlbxBoardTitle: React.FC = () => {
  const [name, setName] = useState<string | null>(null);
  const [isShare, setIsShare] = useState(false);
  const [wsSlug, setWsSlug] = useState<string | null>(null);
  // Autosave status, fed by PlbxBoardSync via the `plbx:autosave` window event.
  // Seed from the window mirror in case an event fired before we subscribed.
  const [saveStatus, setSaveStatus] = useState<
    "saving" | "saved" | "error" | null
  >(() =>
    typeof window !== "undefined"
      ? ((window as any).__plbxSaveStatus ?? null)
      : null,
  );

  useEffect(() => {
    const hash = window.location.hash;
    const shareToken = hash.startsWith("#board=") ? hash.slice(7) : null;

    if (shareToken) {
      setIsShare(true);
      void getSharedScene(shareToken)
        .then((d) => setName(d.name))
        .catch(() => undefined);
      return;
    }

    // Team flow — poll window.__plbxBoardName until PlbxBoardSync fills it.
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const n: string | undefined = (window as any).__plbxBoardName;
      const id: string | undefined = (window as any).__plbxBoardId;
      const slug: string | undefined = (window as any).__plbxBoardWorkspaceSlug;
      if (id && n) {
        setName(n);
        if (slug) setWsSlug(slug);
      } else {
        setTimeout(tick, 300);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  // Subscribe to autosave status (team boards only).
  useEffect(() => {
    const onStatus = (e: Event) => {
      const status = (e as CustomEvent).detail?.status;
      if (status) setSaveStatus(status);
    };
    window.addEventListener("plbx:autosave", onStatus as EventListener);
    return () =>
      window.removeEventListener("plbx:autosave", onStatus as EventListener);
  }, []);

  const rename = async () => {
    if (isShare) return;
    const id: string | undefined = (window as any).__plbxBoardId;
    if (!id || !name) return;
    const next = window.prompt("Rename board:", name);
    if (!next || !next.trim() || next.trim() === name) return;
    try {
      const updated = await patchScene(id, { name: next.trim() });
      (window as any).__plbxBoardName = updated.name;
      setName(updated.name);
    } catch (e) {
      // silent — surfacing errors here would interrupt drawing
    }
  };

  const wsLabel = (slug: string) =>
    slug.startsWith("personal:")
      ? "Personal"
      : slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  if (!name) return null;

  return (
    <div
      className="plbx-board-title"
      title={isShare ? "Read-only share" : "Click to rename"}
    >
      {isShare && <span className="plbx-board-title__badge">shared</span>}
      {wsSlug && !isShare && (
        <>
          <a
            className="plbx-board-title__crumb"
            href={`/boards?workspace=${encodeURIComponent(wsSlug)}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {wsLabel(wsSlug)}
          </a>
          <span className="plbx-board-title__sep">›</span>
        </>
      )}
      <span
        className="plbx-board-title__text"
        onClick={rename}
        role={isShare ? undefined : "button"}
      >
        {name}
      </span>
      {!isShare && saveStatus && (
        <span
          className={`plbx-board-title__status plbx-board-title__status--${saveStatus}`}
          title={
            saveStatus === "error"
              ? "Last autosave failed — keep the tab open"
              : "Changes save automatically"
          }
        >
          {saveStatus === "saving"
            ? "Saving…"
            : saveStatus === "error"
              ? "Save failed"
              : "Saved"}
        </span>
      )}
    </div>
  );
};

export default PlbxBoardTitle;
